import { FieldErrorsImpl, FieldValues, GlobalError } from 'react-hook-form'
import { debounce, isEmpty, map as mapSpecificKey, noop } from 'lodash'
import React, { useEffect, useRef, useState, KeyboardEvent } from 'react'

import { SvgCross, SvgFilledCross, SvgFunnel } from '../../assets/images'
import { formatValueWithPattern, NAMESPACES } from '../../utils'
import Tooltip from '../Tooltip'
import CustomButton from '../CustomButton'

interface DropdownType<T> {
  value: string
  label: string
  disabled?: boolean
  [key: string]: T | string | boolean | undefined
}

interface OptionContainerPropsType<T> {
  option: DropdownType<T>
  handleValueSelect: (e: DropdownType<T>) => void
  isSelected: boolean
  dataQaId?: string
  className?: string
  index: number
  focusedIndex: number
  handleKeyDown: (
    e: KeyboardEvent<HTMLLIElement>,
    option: DropdownType<T>
  ) => void
}

const OptionContainer = <T,>({
  option,
  handleValueSelect,
  isSelected,
  dataQaId,
  className,
  index,
  focusedIndex,
  handleKeyDown,
}: OptionContainerPropsType<T>) => {
  const optionRef = useRef<HTMLLIElement>(null)

  const handleSelected = () => {
    if (!isEmpty(option)) {
      handleValueSelect(option)
    }
  }

  useEffect(() => {
    if (index === focusedIndex && optionRef.current) {
      optionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
      optionRef?.current.focus()
    }
  }, [focusedIndex, index])

  return (
    <li
      ref={optionRef}
      className={`p-3 border-t border-[#E3E3E3] hover:bg-blue-100 flex items-start pointer-events-auto cursor-pointer w-full text-black focus:bg-blue-100 ${
        className ?? ''
      }`}
      onClick={handleSelected}
      title={option?.label}
      data-qa-id={`${dataQaId}-option-box-${option?.value}`}
      tabIndex={index === focusedIndex ? 0 : -1}
      onKeyDown={(e) => handleKeyDown(e, option)}
      role="option"
      aria-selected={isSelected}
    >
      <input
        type="checkbox"
        className="w-[1.125rem] h-[1.125rem] accent-[#007CF0]"
        checked={isSelected}
        onChange={noop}
        tabIndex={-1}
      />
      <span className="max-w-[calc(100%-0.938rem)] pl-3 truncate text-xs leading-4.5">
        <span className="truncate">{option?.label}</span>
        {option?.subLabel ? (
          <div className="truncate text-xs mt-1 font-normal">
            {option.subLabel as string}
          </div>
        ) : null}
      </span>
    </li>
  )
}

interface OptionBoxPropsType<T> {
  dropdownOptions: DropdownType<T>[]
  handleValueSelect: (e: DropdownType<T>) => void
  handleOptionFalse: (e?: DropdownType<T>[]) => void
  isOpenOptions: boolean
  selectedOption: DropdownType<T>[]
  handleSelectAll: (e: boolean) => void
  dataQaId?: string
  isFromFilter?: boolean
  dropdownWrapperRef?: React.RefObject<HTMLDivElement>
  tabIndex?: number
  onFocusClick?: (
    searchValue: string,
    page: number,
    size: number
  ) =>
    | Promise<{ data: string[]; size: number }>
    | { data: string[]; size: number }
  size: number
  hasMore?: boolean
  setHasMore?: React.Dispatch<React.SetStateAction<boolean>>
  searchValue?: React.MutableRefObject<string>
  page: React.MutableRefObject<number>
}

const OptionBox = <T,>({
  dropdownOptions,
  handleValueSelect,
  handleOptionFalse,
  isOpenOptions,
  selectedOption,
  handleSelectAll,
  dataQaId,
  isFromFilter = false,
  dropdownWrapperRef,
  tabIndex,
  onFocusClick,
  size,
  hasMore,
  setHasMore,
  searchValue,
  page,
}: OptionBoxPropsType<T>) => {
  const dropdownOptionRef = useRef<HTMLDivElement>(null)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const optionsListRef = useRef<HTMLUListElement>(null)
  const applyButtonRef = useRef<HTMLButtonElement>(null)
  const clearButtonRef = useRef<HTMLButtonElement>(null)
  const [loading, setLoading] = useState(false)

  const onSelectAll = () => {
    handleSelectAll(
      dropdownOptions.find(
        (option) =>
          selectedOption.find(
            (selOption) => option.value === selOption.value
          ) === undefined
      ) === undefined
    )
  }

  // Handle keyboard navigation within options list
  const handleKeyDown = (
    e: KeyboardEvent<HTMLLIElement>,
    option: DropdownType<T>
  ) => {
    switch (e.key) {
      case 'Enter':
        if (option.value === 'Select All') {
          onSelectAll()
        } else {
          handleValueSelect(option)
        }
        e.preventDefault()
        e.stopPropagation()
        break
      case 'ArrowDown':
        setFocusedIndex((prev) => Math.min(prev + 1, dropdownOptions.length))
        e.preventDefault()
        break
      case 'ArrowUp':
        setFocusedIndex((prev) => Math.max(prev - 1, 0))
        e.preventDefault()
        break
      case 'Tab':
        if (isFromFilter) {
          if (e.shiftKey) {
            // Shift+Tab moves focus back to last option
            setFocusedIndex(dropdownOptions.length - 1)
          } else {
            // Tab moves focus to clear button
            e.preventDefault()
            clearButtonRef.current?.focus()
          }
        }
        break
      case 'Escape':
        handleOptionFalse()
        e.preventDefault()
        break
    }
  }

  // Focus first option when dropdown opens
  useEffect(() => {
    if (isOpenOptions && dropdownOptions.length > 0) {
      setFocusedIndex(-1)
    }
  }, [isOpenOptions])

  // Function to handle closing the menu and returning focus to the dropdown
  const handleCloseAndFocus = (newOptions?: DropdownType<T>[]) => {
    handleOptionFalse(newOptions)
    dropdownWrapperRef?.current?.focus() // Do it immediately instead of waiting
  }

  useEffect(() => {
    const ul = optionsListRef.current
    if (!ul) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = ul
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 50
      if (isNearBottom && !loading && hasMore && onFocusClick) {
        setLoading(true)
        const nextPage = page?.current + 1

        Promise.resolve(
          onFocusClick?.(searchValue?.current ?? '', nextPage, size)
        )
          .then((result) => {
            if (!result?.data || result?.size < size) {
              setHasMore && setHasMore(false)
            } else {
              page.current = nextPage
            }
          })
          .finally(() => setLoading(false))
      }
    }

    ul.addEventListener('scroll', handleScroll)
    return () => ul.removeEventListener('scroll', handleScroll)
  }, [page, loading, hasMore, size])

  return (
    <div
      ref={dropdownOptionRef}
      className={`flex font-medium absolute mt-[0.006rem] mr-5 w-full rounded-b z-[9999] border-r border-l border-b bg-white max-h-[12.875rem] group-hover:border-t-0 
           ${isOpenOptions ? 'border-blue-500' : ' border-gray-600'}
          group-hover:border-blue-500`}
      role="listbox"
      aria-multiselectable="true"
    >
      <div className="flex flex-col flex-1 min-h-0 min-w-0">
        <ul
          ref={optionsListRef}
          className="flex-1 min-h-0 overflow-y-auto border-t border-border-100"
          role="group"
        >
          {dropdownOptions.length ? (
            <OptionContainer
              key={`select-all-${dropdownOptions.length}`}
              option={{ label: 'Select All', value: 'Select All' }}
              handleValueSelect={onSelectAll}
              dataQaId={dataQaId}
              isSelected={
                dropdownOptions.find(
                  (option) =>
                    selectedOption.find(
                      (selOption) => option.value === selOption.value
                    ) === undefined
                ) === undefined
              }
              className="border-t-0"
              index={0}
              focusedIndex={focusedIndex}
              handleKeyDown={handleKeyDown}
            />
          ) : null}
          {dropdownOptions.map((option: DropdownType<T>, index: number) => (
            <OptionContainer
              key={`${option?.value}_${index}`}
              option={option}
              handleValueSelect={handleValueSelect}
              dataQaId={dataQaId}
              isSelected={mapSpecificKey(selectedOption, 'value').includes(
                option?.value
              )}
              index={index + 1} // +1 because "Select All" is at index 0
              focusedIndex={focusedIndex}
              handleKeyDown={handleKeyDown}
            />
          ))}
          {loading && (
            <li className="p-3 text-center text-sm text-gray-500">
              {NAMESPACES.LOADING}
            </li>
          )}
        </ul>
        {dropdownOptions.length && isFromFilter ? (
          <div className="flex justify-around py-2 border-t border-border-200 px-4 gap-4">
            <CustomButton
              type="button"
              onClick={() => {
                handleCloseAndFocus([])
              }}
              color="primary"
              className="border text-xs flex-1 h-[1.75rem] font-light"
              dataQaId="filter-options-cancel-button"
              ref={clearButtonRef}
              tabIndex={tabIndex}
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  if (e.shiftKey) {
                    // Shift+Tab moves focus to the last option
                    e.preventDefault()
                    setFocusedIndex(dropdownOptions.length)
                  } else {
                    // Tab moves focus to apply button
                    e.preventDefault()
                    applyButtonRef.current?.focus()
                  }
                } else if (e.key === 'Enter') {
                  handleCloseAndFocus([])
                }
              }}
            >
              Clear
            </CustomButton>
            <CustomButton
              type="button"
              onClick={() => {
                handleCloseAndFocus()
              }}
              isFilled={true}
              color="primary"
              className="border text-xs flex-1 h-[1.75rem] font-light"
              dataQaId="filter-options-apply-button"
              ref={applyButtonRef}
              tabIndex={tabIndex}
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  if (e.shiftKey) {
                    // Shift+Tab moves focus to clear button
                    e.preventDefault()
                    clearButtonRef.current?.focus()
                  }
                } else if (e.key === 'Enter') {
                  handleCloseAndFocus()
                }
              }}
            >
              Apply
            </CustomButton>
          </div>
        ) : null}
      </div>
    </div>
  )
}

interface FieldErrors<U extends FieldValues> {
  root?: Record<string, GlobalError> & GlobalError
  [key: string]: FieldErrorsImpl<U> | GlobalError | undefined
}

interface MultiSelectPropTypes<T, U extends FieldValues> {
  label: string
  options: DropdownType<T>[]
  name: string
  defaultSelected?: DropdownType<T>[]
  disabled?: boolean
  required?: boolean
  onChange?: (value: DropdownType<T>[], name: string) => void
  className?: string
  errors?: FieldErrors<U>
  noOfChips?: number
  onSearch?: (searchTerm: string) => void
  onFocusOut?: (e: DropdownType<T>[]) => void
  inpref?: React.RefObject<HTMLInputElement | null>
  dataQaId?: string
  allowCustomValues?: boolean
  format?: string
  showCrossIcon?: boolean
  excludedParentRef: React.MutableRefObject<HTMLElement | null>
  isFilterApplied?: boolean
  isFromFilter?: boolean
  tabIndex?: number
  isFilterDisabled?: boolean
  onFocusClick?: (
    searchValue: string,
    page: number,
    size: number
  ) =>
    | Promise<{ data: string[]; size: number }>
    | { data: string[]; size: number }
  size?: number
}

const MultiSelectSearchDropDown = <T, U extends FieldValues>({
  label = '',
  name = '',
  options,
  disabled = false,
  required = false,
  onChange = noop,
  className = '',
  errors,
  noOfChips = 0,
  onSearch = noop,
  onFocusOut = noop,
  defaultSelected,
  inpref,
  dataQaId,
  allowCustomValues = true,
  format,
  showCrossIcon = false,
  excludedParentRef,
  isFilterApplied,
  isFromFilter = false,
  tabIndex = 0,
  isFilterDisabled = false,
  onFocusClick,
  size = 10,
}: MultiSelectPropTypes<T, U>) => {
  const [isOpenOptions, setIsOpenOptions] = useState(false)
  const [isOpenSelectedOptions, setIsOpenSelectedOptions] = useState<{
    open: boolean
    options: DropdownType<T>[]
  }>({
    open: false,
    options: [],
  })
  const [selectedOption, setSelectedOption] = useState<DropdownType<T>[]>(
    defaultSelected ?? []
  )
  const selectedOptionRef = useRef<DropdownType<T>[]>()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownWrapperRef = useRef<HTMLDivElement>(null)
  const ListArr = new Array(noOfChips + 1).fill('1')
  const [hasMore, setHasMore] = useState<boolean>(true)
  const searchValue = useRef<string>('')
  const page = useRef<number>(1)

  let inputRef = useRef<HTMLInputElement | null>(null)
  if (inpref) inputRef = inpref

  const handleOptionTrue = (
    e?: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (
      e &&
      ((e.target as HTMLElement).classList.contains('cross-icon') ||
        (e.target as HTMLElement).parentElement?.classList.contains(
          'cross-icon'
        ))
    ) {
      setIsOpenOptions(false)
      setIsOpenSelectedOptions({ open: false, options: [] })
      handleOptionFalse([])
      return
    }

    if (isOpenSelectedOptions.open)
      setIsOpenSelectedOptions({ open: false, options: [] })

    setIsOpenOptions(true)
    inputRef.current?.focus() // Keep focus on input

    // Don't auto-focus the first option
  }

  const handleOptionFalse = (e?: DropdownType<T>[]) => {
    setIsOpenOptions(false)
    if (inputRef.current) inputRef.current.value = ''
    if (e) {
      setSelectedOption(e)
      selectedOptionRef.current = e
      onChange(e, name)
      onFocusOut(e)
    } else onFocusOut(selectedOption)

    // Return focus to the dropdown wrapper
    setTimeout(() => {
      dropdownWrapperRef.current?.focus()
    }, 10)
  }

  const handleOptionSelectedFalse = (e?: DropdownType<T>[]) => {
    setIsOpenSelectedOptions({ open: false, options: [] })
    if (inputRef.current) inputRef.current.value = ''
    if (e) {
      setSelectedOption(e)
      selectedOptionRef.current = e
      onChange(e, name)
      onFocusOut(e)
    } else onFocusOut(selectedOption)

    // Return focus to the dropdown wrapper
    setTimeout(() => {
      dropdownWrapperRef.current?.focus()
    }, 10)
  }

  const handleValueSelect = (selectedValue: DropdownType<T>) => {
    if (
      mapSpecificKey(selectedOption, 'value').includes(selectedValue?.value)
    ) {
      const fieldIndex = mapSpecificKey(selectedOption, 'value').indexOf(
        selectedValue?.value
      )
      const slicedOption = [...selectedOption]
      slicedOption.splice(fieldIndex, 1)
      onChange(slicedOption, name)
      setSelectedOption([...slicedOption])
      selectedOptionRef.current = [...slicedOption]
    } else {
      onChange([...selectedOption, selectedValue], name)
      setSelectedOption([...selectedOption, selectedValue])
      selectedOptionRef.current = [...selectedOption, selectedValue]
    }
  }

  const handleSelectAll = (e: boolean) => {
    if (e) {
      if (isOpenSelectedOptions.open) {
        onChange([], name)
        setSelectedOption([])
        selectedOptionRef.current = []
      } else {
        const map = new Map()
        selectedOption.forEach((option) => map.set(option.value, option))
        options.forEach((option) => {
          if (map.has(option.value)) map.delete(option.value)
        })
        const unionArray = [...map.values()]
        onChange(unionArray, name)
        setSelectedOption(unionArray)
        selectedOptionRef.current = unionArray
      }
    } else if (isOpenSelectedOptions.open) {
      onChange(isOpenSelectedOptions.options, name)
      setSelectedOption(isOpenSelectedOptions.options)
      selectedOptionRef.current = isOpenSelectedOptions.options
    } else {
      const map = new Map()
      ;[...selectedOption, ...options].forEach((option) =>
        map.set(option.value, option)
      )
      const unionArray = [...map.values()]
      onChange(unionArray, name)
      setSelectedOption(unionArray)
      selectedOptionRef.current = unionArray
    }
  }

  const clearChip = (index: number) => {
    if (!isOpenSelectedOptions.open) {
      setIsOpenSelectedOptions({ open: true, options: selectedOption })
    }
    if (isOpenOptions) setIsOpenOptions(false)
    const cloneSelectedValue = structuredClone(selectedOption)
    cloneSelectedValue.splice(index, 1)
    setSelectedOption(cloneSelectedValue)
    selectedOptionRef.current = cloneSelectedValue
    onChange(cloneSelectedValue, name)
    inputRef?.current?.focus()
  }

  const removePhoneMasking = (value: string) => {
    const digits = value.replace(/-/g, '')

    // Validate it's exactly 10 digits
    if (/^\d{10}$/.test(digits)) {
      return `+1 ${digits}`
    }
    return digits
  }

  const debouncedSearch = debounce((value: string) => {
    const formattedValue = value?.trim()
    if (formattedValue.length >= 3 || !formattedValue.length) {
      if (format) {
        onSearch(removePhoneMasking(formattedValue))
        page.current = 1
        searchValue.current = removePhoneMasking(formattedValue)
      } else {
        onSearch(formattedValue)
        searchValue.current = formattedValue
        page.current = 1
        setHasMore(true)
      }
    } else {
      searchValue.current = ''
      page.current = 1
      setHasMore(true)
    }
  }, 300)

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (inputRef.current && format)
      inputRef.current.value = formatValueWithPattern(value, format)
    if (value.length < 1 && !isOpenOptions) {
      onSearch('')
      page.current = 1
      setHasMore(true)
    } else {
      debouncedSearch(value)
    }
  }

  // Handle keyboard navigation for the main dropdown component
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return

    switch (e.key) {
      case 'Enter':
      case 'ArrowDown':
        if (!isOpenOptions && !isOpenSelectedOptions.open) {
          handleOptionTrue()
          e.preventDefault()
        }
        break
      case 'Escape':
        if (isOpenOptions || isOpenSelectedOptions.open) {
          setIsOpenOptions(false)
          setIsOpenSelectedOptions({ open: false, options: [] })
          e.preventDefault()
        }
        break
    }
  }

  // useEffect(() => {
  //   inpref?.current?.addEventListener('focus', () => setIsOpenOption(true))
  // }, [])

  useEffect(() => {
    if (isOpenOptions) setIsOpenSelectedOptions({ open: false, options: [] })
  }, [isOpenOptions])

  useEffect(() => {
    if (defaultSelected) {
      setSelectedOption(defaultSelected)
      selectedOptionRef.current = defaultSelected
    }
  }, [defaultSelected])

  // Handle Enter key press on chip cross button
  const handleChipKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      clearChip(index)

      // Return focus to the dropdown wrapper
      setTimeout(() => {
        dropdownWrapperRef.current?.focus()
      }, 10)
    }
  }
  const handleFocusClick = () => {
    if (onFocusClick && !isOpenOptions) {
      searchValue.current = ''
      page.current=1
      Promise.resolve(onFocusClick?.(searchValue.current, page.current, size)).then(
        (result) => {
          if (!result?.data || result?.size < size) {
            setHasMore(false)
          } else {
            setHasMore(true)
          }
        }
      )
    }
  }

  return (
    <div
      ref={dropdownRef}
      className={`group relative w-full font-primary ${className} cursor-pointer select-none focus:outline-none`}
      data-qa-id={`${dataQaId}-container`}
      tabIndex={-1}
      onBlur={(e) => {
        if (
          !e.relatedTarget ||
          (!excludedParentRef.current?.contains(e.relatedTarget) &&
            !dropdownRef.current?.contains(e.relatedTarget))
        ) {
          setIsOpenSelectedOptions({ open: false, options: [] })
          setIsOpenOptions(false)
          if (inputRef.current) inputRef.current.value = ''
          onFocusOut(selectedOptionRef.current ?? [])
        }
      }}
      role="combobox"
      aria-expanded={isOpenOptions || isOpenSelectedOptions.open}
      aria-haspopup="listbox"
      aria-controls="dropdown-listbox"
      aria-required={required}
    >
      <div
        ref={dropdownWrapperRef}
        className="max-h-10 w-full box-border relative bg-white items-center rounded focus-within:outline-none"
        tabIndex={disabled ? -1 : tabIndex}
        onKeyDown={handleKeyDown}
        aria-label={`${label} dropdown`}
      >
        {label ? (
          <div
            className={`translate-x-[0.875rem] translate-y-[-0.75rem] flex text-base p-0 origin-top-left whitespace-nowrap overflow-hidden text-ellipsis max-w-[calc(100%-1.75rem)] absolute left-0 top-0 z-10 pointer-events-none bg-white px-1 ${
              disabled ? 'text-accent-300' : 'text-black'
            }`}
          >
            <label
              className={`font-medium block text-xs leading-1 font-primary whitespace-nowrap overflow-hidden text-ellipsis max-w-[calc(100% - 1.5rem)] 
                ${isOpenOptions ? 'text-blue-500' : ''}
               ${
                 disabled
                   ? 'text-accent-300'
                   : 'group-hover:text-secondary-500 group-focus-within:text-secondary-500'
               }`}
              title={label}
              id={`${dataQaId}-label`}
            >
              {label}
            </label>
            {required ? (
              <span
                className={`ml-1 text-xs leading-1 ${
                  disabled ? 'text-accent-300' : 'text-error-500'
                }`}
              >
                *
              </span>
            ) : null}
          </div>
        ) : null}
        <div
          className={`${
            disabled ? 'z-10' : 'z-0'
          } pl-2.5 pr-2.5 pt-2 pb-2 relative`}
          data-qa-id={`${dataQaId}-dropdown-box`}
          onClick={(e) => handleOptionTrue(e)}
        >
          <div className="flex items-center w-full min-w-[15.5rem]">
            {isFromFilter && !isFilterDisabled ? (
              <SvgFunnel
                width={'16'}
                height={'16'}
                className={`cursor-default ${
                  isFilterApplied
                    ? 'svg-path-fill-secondary'
                    : 'svg-path-fill-accent-300'
                }
                `}
              />
            ) : null}
            <div className="flex-1 flex pl-1.5">
              {!isEmpty(selectedOption) ? (
                <div
                  className={`font-medium items-center h-[1.375rem] flex flex-wrap gap-1
                  ${disabled ? 'text-accent-500' : ''}
              text-black pr-1`}
                >
                  {(isOpenOptions
                    ? ListArr.slice(0, ListArr.length - 1)
                    : ListArr
                  )?.map((val, index) => (
                    <React.Fragment key={index + '' + val}>
                      {index < selectedOption.length ? (
                        <Tooltip content={selectedOption?.[index]?.label}>
                          <div
                            className="bg-secondary-50 text-accent-500 flex justify-center items-center py-0.5 px-2 rounded-full"
                            role="listitem"
                            tabIndex={-1}
                          >
                            <span
                              className={`leading-4 text-xs ${
                                selectedOption?.[index]?.label.length < 8
                                  ? ''
                                  : 'truncate'
                              } max-w-[5.25rem] font-primary`}
                            >
                              {selectedOption?.[index]?.label}
                            </span>
                            <div
                              className="p-[0.0625rem]"
                              data-qa-id={`${dataQaId}-clear-chip-${selectedOption?.[index]?.label}`}
                              onClick={(e) => {
                                clearChip(index)
                                e.stopPropagation()
                              }}
                              tabIndex={tabIndex}
                              onKeyDown={(e) => handleChipKeyDown(e, index)}
                              role="button"
                              aria-label={`Remove ${selectedOption?.[index]?.label}`}
                            >
                              <SvgFilledCross />
                            </div>
                          </div>
                        </Tooltip>
                      ) : null}
                    </React.Fragment>
                  ))}
                  {selectedOption?.length -
                    (isOpenOptions ? ListArr?.length - 1 : ListArr?.length) >
                  0 ? (
                    <div
                      className="bg-secondary-50 text-accent-500 flex gap-1 justify-center items-center  py-0.5 px-2 mr-1 rounded-full"
                      data-qa-id={`${dataQaId}-show-selected-option`}
                      onClick={(e) => {
                        if (isOpenOptions) setIsOpenOptions(false)
                        setIsOpenSelectedOptions((prev) => {
                          if (prev.open) return { open: false, options: [] }
                          else
                            return { open: true, options: [...selectedOption] }
                        })
                        e.stopPropagation()
                      }}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (isOpenOptions) setIsOpenOptions(false)
                          setIsOpenSelectedOptions((prev) => {
                            if (prev.open) return { open: false, options: [] }
                            else
                              return {
                                open: true,
                                options: [...selectedOption],
                              }
                          })
                          e.stopPropagation()
                        }
                      }}
                      role="button"
                      aria-label="Show more selected options"
                    >
                      <span className="leading-4 text-xs max-w-[2.625rem] overflow-hidden text-ellipsis">
                        +
                        {selectedOption?.length -
                          (isOpenOptions
                            ? ListArr?.length - 1
                            : ListArr?.length)}
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <input
                type="text"
                className={`w-full focus-within:outline-none flex-1 ${
                  isOpenOptions ? 'min-w-20' : 'min-w-4'
                } text-xs py-0.5 flex-shrink flex-grow`}
                ref={inputRef}
                data-qa-id={`${dataQaId}-search-input`}
                onChange={handleSearch}
                onFocus={handleFocusClick}
                disabled={disabled}
                onKeyDown={(e) => {
                  if (e.key === 'Tab') return
                  if (e.key === 'Enter') {
                    if (!isOpenOptions) {
                      // If dropdown is closed, open it without moving focus
                      handleOptionTrue()
                      e.preventDefault()
                    } else if (allowCustomValues) {
                      const value = (e.target as HTMLInputElement).value?.trim()
                      if (value?.length) {
                        let selectedOptionArray: DropdownType<T>[] = [
                          ...selectedOption,
                        ]
                        if (mapSpecificKey(options, label).includes(value)) {
                          const selectedValue = options.find(
                            (op) => op.label === value
                          )
                          if (
                            selectedValue &&
                            !selectedOption.find(
                              (option) => option.value === selectedValue.value
                            )
                          )
                            selectedOptionArray = [
                              ...selectedOption,
                              selectedValue,
                            ]
                        } else if (
                          !selectedOption.find(
                            (option) => option.value === value
                          )
                        ) {
                          selectedOptionArray = [
                            ...selectedOption,
                            {
                              label: value,
                              value: format ? removePhoneMasking(value) : value,
                            },
                          ]
                        }
                        handleOptionFalse(selectedOptionArray)
                        e.preventDefault()
                      }
                    }
                  } else if (e.key === 'ArrowDown') {
                    // Open dropdown on arrow down if not already open
                    if (!isOpenOptions && !isOpenSelectedOptions.open) {
                      handleOptionTrue()
                      e.preventDefault()
                    } else if (isOpenOptions) {
                      // If dropdown is already open, try to focus the first option
                      const firstOption =
                        dropdownRef.current?.querySelector('li[role="option"]')
                      if (firstOption) {
                        ;(firstOption as HTMLElement).focus()
                      }
                      e.preventDefault()
                    }
                  } else if (e.key === 'Escape') {
                    // Close dropdown on Escape
                    if (isOpenOptions || isOpenSelectedOptions.open) {
                      setIsOpenOptions(false)
                      setIsOpenSelectedOptions({ open: false, options: [] })
                      e.preventDefault()
                    }
                  }
                }}
                tabIndex={tabIndex}
                aria-labelledby={`${dataQaId}-label`}
                aria-autocomplete="list"
              />
            </div>
            {!isFilterDisabled && (selectedOption?.length || showCrossIcon) ? (
              <div
                className="px-[0.5625rem] cross-icon"
                role="button"
                tabIndex={tabIndex}
                aria-label="Clear all selections"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleOptionFalse([])
                  }
                }}
              >
                <SvgCross
                  height={'10'}
                  width={'10'}
                  className="svg-path-fill-secondary cross-icon"
                />
              </div>
            ) : null}
          </div>
        </div>
        <fieldset
          aria-hidden="true"
          className={`text-left border absolute outline-none z-0 ${
            label ? 'top-[-0.938rem]' : 'top-0'
          } bottom-0 left-0 right-0 px-2 pointer-events-none rounded border-solid overflow-hidden min-w-0 
                    border-border-150
                    
                    ${
                      isOpenOptions || isOpenSelectedOptions.open
                        ? 'border-b-0 rounded-b-none group-hover:border-b-0 group-hover:rounded-b-none border-secondary-500'
                        : ''
                    }
                    ${
                      disabled
                        ? 'border-border-200 bg-accent-50'
                        : 'group-hover:border-secondary-500 group-focus-within:border-secondary-500'
                    }
                    ${
                      !isEmpty(errors?.[name])
                        ? 'border-error-500'
                        : 'border-border-150'
                    }`}
        >
          {label ? (
            <legend className="invisible flex float-none max-w-full select-none p-1 ">
              <span
                className={`font-medium dropdown-label overflow-hidden whitespace-nowrap text-ellipsis  
                        ${isOpenOptions ? 'text-secondary-500' : ''}
                        ${
                          disabled
                            ? 'text-accent-300'
                            : 'group-hover:text-secondary-500 '
                        }`}
              >
                {label}
              </span>
              {required ? (
                <span
                  className={`ml-1 text-xs leading-1  ${
                    disabled ? 'text-accent-300' : 'text-error-500'
                  }`}
                >
                  *
                </span>
              ) : null}
            </legend>
          ) : null}
        </fieldset>
      </div>

      {isOpenOptions ? (
        <OptionBox
          handleValueSelect={handleValueSelect}
          handleOptionFalse={handleOptionFalse}
          dropdownOptions={options}
          isOpenOptions={isOpenOptions}
          selectedOption={selectedOption}
          handleSelectAll={handleSelectAll}
          isFromFilter={isFromFilter}
          dataQaId={dataQaId}
          dropdownWrapperRef={dropdownWrapperRef}
          tabIndex={tabIndex}
          onFocusClick={onFocusClick}
          size={size}
          hasMore={hasMore}
          setHasMore={setHasMore}
          searchValue={searchValue}
          page={page}
        />
      ) : null}
      {isOpenSelectedOptions.open ? (
        <OptionBox
          handleValueSelect={handleValueSelect}
          handleOptionFalse={handleOptionSelectedFalse}
          dropdownOptions={isOpenSelectedOptions.options}
          isOpenOptions={isOpenSelectedOptions.open}
          selectedOption={selectedOption}
          handleSelectAll={handleSelectAll}
          isFromFilter={isFromFilter}
          dataQaId={dataQaId}
          dropdownWrapperRef={dropdownWrapperRef}
          tabIndex={tabIndex}
          onFocusClick={onFocusClick}
          size={size}
          hasMore={hasMore}
          setHasMore={setHasMore}
          searchValue={searchValue}
          page={page}
        />
      ) : null}

      {errors &&
        name &&
        errors?.[name] &&
        errors?.[name]?.['type'] === 'required' &&
        !errors?.[name]?.message && (
          <span className="text-error-500 text-xs" role="alert">
            Required
          </span>
        )}
      {errors &&
        name &&
        errors?.[name] &&
        errors?.[name]?.['type'] !== 'required' &&
        !errors?.[name]?.message && (
          <span className="text-error-500 text-xs" role="alert">
            {errors?.[name]?.['message']?.toString()}
          </span>
        )}
    </div>
  )
}

export default MultiSelectSearchDropDown
